import { useCallback } from 'react';
import { useToggle } from '@dfl/hook-utils';
import { useParamsLink } from '@dfl/react-security';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDeleteAudit } from 'modules/audit/management/hooks/useDeleteAudit';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AUDITS_LIST_KEY } from '../constants';
import { AuditService } from '../services';

const useTableMenuOptions = (rowId: string | undefined) => {
  const navigate = useNavigate();
  const { t } = useTranslation('audit');

  const { isOpen, onClose, onOpen } = useToggle();
  const { mutate, isLoading, error } = useDeleteAudit(rowId || '', onClose);
  const queryClient = useQueryClient();
  const { mutate: duplicateAudit } = useMutation((rowId: string) => AuditService.duplicateAudit(rowId), {
    onSuccess: () => {
      queryClient.invalidateQueries([AUDITS_LIST_KEY]);
      toast.success(t('auditDuplicated'));
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleConfirm = useCallback(() => {
    mutate();
  }, [mutate]);

  const handleContinue = useCallback(() => {
    navigate(`/quiz/audit/${rowId as string}`);
  }, [navigate, rowId]);

  const handleEdit = useParamsLink({ edit: rowId });
  const handleDetails = () => {
    if (rowId) {
      navigate(`/audit/${rowId}/general`);
    }
  };

  const handleDuplicate = useCallback(() => {
    duplicateAudit(rowId as string);
  }, [duplicateAudit, rowId]);

  return {
    handleEdit,
    handleContinue,
    handleDuplicate,
    handleDetails,
    handleConfirm,
    isLoading,
    error,
    isOpen,
    onClose,
    onOpen,
  };
};

export default useTableMenuOptions;
